import styled from 'styled-components';
import { useAuth } from '../../hooks/useAuth';
import { usePermissions } from '../../hooks/usePermissions';
import { useGetUserProfileQuery } from '../../store/api/userApi';
import { Badge } from '../../styles/components';
import { 
  Bars3Icon,
  UserCircleIcon,
  ArrowRightOnRectangleIcon 
} from '@heroicons/react/24/outline';

const HeaderContainer = styled.div`
  position: sticky;
  top: 0;
  z-index: 40;
  width: 100vw;
  margin: 0;
  padding: 0;
`;

const HeaderContent = styled.div`
  display: flex;
  height: 4rem;
  width: 100%;
  align-items: center;
  gap: ${props => props.theme.spacing.md};
  border-bottom: 1px solid ${props => props.theme.colors.gray[200]};
  background-color: white;
  padding: 0 ${props => props.theme.spacing.md};
  box-shadow: ${props => props.theme.shadows.sm};
  
  @media (min-width: 640px) {
    gap: ${props => props.theme.spacing.xl};
    padding: 0 ${props => props.theme.spacing.xl};
  }
  
  @media (min-width: 1024px) {
    padding: 0 ${props => props.theme.spacing['2xl']};
    box-shadow: ${props => props.theme.shadows.sm};
  }
`;

const MobileMenuButton = styled.button`
  margin: -0.625rem;
  padding: 0.625rem;
  color: ${props => props.theme.colors.gray[700]};
  
  @media (min-width: 1024px) {
    display: none;
  }
`;

const Divider = styled.div`
  height: 1.5rem;
  width: 1px;
  background-color: ${props => props.theme.colors.gray[200]};
  
  @media (min-width: 1024px) {
    display: none;
  }
`;

const FlexContainer = styled.div`
  display: flex;
  flex: 1;
  gap: ${props => props.theme.spacing.md};
  align-self: stretch;
  
  @media (min-width: 1024px) {
    gap: ${props => props.theme.spacing.xl};
  }
`;

const Spacer = styled.div`
  position: relative;
  display: flex;
  flex: 1;
`;

const UserSection = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.md};
  
  @media (min-width: 1024px) {
    gap: ${props => props.theme.spacing.xl};
  }
`;

const UserInfo = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
`;

const UserIcon = styled(UserCircleIcon)`
  height: 2rem;
  width: 2rem;
  color: ${props => props.theme.colors.gray[400]};
`;

const UserDetails = styled.div`
  font-size: ${props => props.theme.fontSizes.sm};
`;

const UserName = styled.div`
  font-weight: ${props => props.theme.fontWeights.medium};
  color: ${props => props.theme.colors.gray[900]};
  line-height: 1.2;
`;

const UserClientId = styled.div`
  font-size: ${props => props.theme.fontSizes.xs};
  color: ${props => props.theme.colors.gray[500]};
  font-family: monospace;
  margin-top: 1px;
`;

const UserScope = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
`;

const ScopeText = styled.span`
  color: ${props => props.theme.colors.gray[500]};
`;

const LogoutButton = styled.button`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
  font-size: ${props => props.theme.fontSizes.sm};
  color: ${props => props.theme.colors.gray[500]};
  transition: color 0.2s ease;
  
  &:hover {
    color: ${props => props.theme.colors.gray[700]};
  }
`;

const LogoutIcon = styled(ArrowRightOnRectangleIcon)`
  height: 1.25rem;
  width: 1.25rem;
`;

export function Header() {
  const { user, logout, isAuthenticated } = useAuth();
  const { isSupport, role } = usePermissions();
  const { data: userProfile } = useGetUserProfileQuery(undefined, {
    skip: !isAuthenticated,
  });

  // Use profile data if available, fallback to auth user data
  const displayName = userProfile?.name || user?.name;
  const clientId = userProfile?.clientId || user?.id;
  const displayScope = userProfile?.scope || user?.scope;
  const displayRole = userProfile?.role || role;

  return (
    <HeaderContainer>
      <HeaderContent>
        <MobileMenuButton type="button">
          <span style={{ position: 'absolute', width: '1px', height: '1px', padding: 0, margin: '-1px', overflow: 'hidden', clip: 'rect(0, 0, 0, 0)', whiteSpace: 'nowrap', border: 0 }}>
            Open sidebar
          </span>
          <Bars3Icon style={{ height: '1.5rem', width: '1.5rem' }} aria-hidden="true" />
        </MobileMenuButton>

        <Divider aria-hidden="true" />

        <FlexContainer>
          <Spacer />
          <UserSection>
            <UserInfo>
              <UserIcon />
              <UserDetails>
                <UserName>{displayName}</UserName>
                <UserClientId>{clientId}</UserClientId>
              </UserDetails>
                <UserScope>
                  {isSupport && (
                    <Badge variant="success">
                      Support
                    </Badge>
                  )}
                  {isSupport && displayRole && (
                    <Badge variant="info">
                      {displayRole}
                    </Badge>
                  )}
                </UserScope>
            </UserInfo>
            <LogoutButton onClick={logout}>
              <LogoutIcon />
              Sign out
            </LogoutButton>
          </UserSection>
        </FlexContainer>
      </HeaderContent>
    </HeaderContainer>
  );
}